"use client";
import { useEffect, useRef, useCallback } from "react";

/**
 * ResizeObserver Hook 的配置选项
 */
interface UseResizeObserverOptions {
  /** 是否启用 ResizeObserver */
  enabled: boolean;
  /** 高度变化时的回调函数 */
  onHeightChange: (itemKey: string, height: number) => void;
  /** 防抖延迟（毫秒），默认为 0（使用 requestAnimationFrame） */
  debounceDelay?: number;
}

/**
 * ResizeObserver Hook 的返回值
 */
interface UseResizeObserverReturn {
  /** 设置要观察的元素的 ref 回调 */
  setObservedElement: (
    itemKey: string,
  ) => (element: HTMLElement | null) => void;
  /** 停止观察指定的元素 */
  unobserve: (itemKey: string) => void;
  /** 停止观察所有元素 */
  disconnect: () => void;
}

/**
 * ResizeObserver Hook
 * 用于自动检测列表项的高度变化
 *
 * @param options - 配置选项
 * @returns ResizeObserver 控制方法
 */
export function useResizeObserver(
  options: UseResizeObserverOptions,
): UseResizeObserverReturn {
  const { enabled, onHeightChange } = options;

  // 存储 ResizeObserver 实例
  const observerRef = useRef<ResizeObserver | null>(null);

  // 存储每个 itemKey 对应的元素
  const elementsRef = useRef<Map<string, HTMLElement>>(new Map());

  // 存储上次的高度，避免重复触发
  const lastHeightsRef = useRef<Map<string, number>>(new Map());

  // 用于批量处理高度更新的 ref
  const pendingUpdatesRef = useRef<Map<string, number>>(new Map());
  const batchUpdateTimerRef = useRef<number | undefined>(undefined);

  /**
   * 批量处理高度更新，减少频繁的状态更新
   */
  const processPendingUpdates = useCallback(() => {
    const updates = pendingUpdatesRef.current;
    if (updates.size === 0) {
      return;
    }

    // 批量处理所有待更新的高度
    updates.forEach((height, itemKey) => {
      onHeightChange(itemKey, height);
    });

    updates.clear();
    batchUpdateTimerRef.current = undefined;
  }, [onHeightChange]);

  /**
   * 处理高度变化
   */
  const handleResize = useCallback(
    (entries: ResizeObserverEntry[]) => {
      entries.forEach((entry) => {
        const element = entry.target as HTMLElement;
        const itemKey = element.getAttribute("data-item-key");

        if (!itemKey) {
          return;
        }

        // 使用 borderBoxSize 获取更稳定的高度（包含 border）
        // 如果不支持，回退到 contentRect.height
        let newHeight: number;
        if (entry.borderBoxSize && entry.borderBoxSize.length > 0) {
          newHeight = entry.borderBoxSize[0].blockSize;
        } else {
          newHeight = entry.contentRect.height;
        }

        const lastHeight = lastHeightsRef.current.get(itemKey);

        // 只有当高度真的变化时才触发回调（使用更大的阈值避免抖动）
        if (lastHeight === undefined || Math.abs(newHeight - lastHeight) > 2) {
          lastHeightsRef.current.set(itemKey, newHeight);

          // 将更新添加到待处理队列
          pendingUpdatesRef.current.set(itemKey, newHeight);

          // 清除旧的批量更新定时器
          if (batchUpdateTimerRef.current !== undefined) {
            cancelAnimationFrame(batchUpdateTimerRef.current);
          }

          // 使用 requestAnimationFrame 批量处理更新
          batchUpdateTimerRef.current = requestAnimationFrame(() => {
            processPendingUpdates();
          });
        }
      });
    },
    [processPendingUpdates],
  );

  /**
   * 初始化 ResizeObserver
   */
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // 创建 ResizeObserver 实例
    observerRef.current = new ResizeObserver(handleResize);

    // 捕获 ref 引用以在清理函数中使用
    const elements = elementsRef.current;
    const lastHeights = lastHeightsRef.current;
    const pendingUpdates = pendingUpdatesRef.current;
    const batchUpdateTimer = batchUpdateTimerRef;

    return () => {
      // 清理：断开所有观察并清除所有定时器
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      // 清除批量更新定时器
      if (batchUpdateTimer.current !== undefined) {
        cancelAnimationFrame(batchUpdateTimer.current);
      }

      elements.clear();
      lastHeights.clear();
      pendingUpdates.clear();
    };
  }, [enabled, handleResize]);

  /**
   * 设置要观察的元素
   * 返回一个 ref 回调函数，可以直接用于 React ref
   */
  const setObservedElement = useCallback(
    (itemKey: string) => (element: HTMLElement | null) => {
      if (!enabled || !observerRef.current) {
        return;
      }

      // 如果之前观察过这个 itemKey，先停止观察
      const oldElement = elementsRef.current.get(itemKey);
      if (oldElement) {
        observerRef.current.unobserve(oldElement);
        elementsRef.current.delete(itemKey);
        // 清除待处理的更新，避免元素卸载后还触发更新
        pendingUpdatesRef.current.delete(itemKey);
      }

      // 如果新元素存在，开始观察
      if (element) {
        elementsRef.current.set(itemKey, element);
        observerRef.current.observe(element);
      }
    },
    [enabled],
  );

  /**
   * 停止观察指定的元素
   */
  const unobserve = useCallback(
    (itemKey: string) => {
      if (!observerRef.current) {
        return;
      }

      const element = elementsRef.current.get(itemKey);
      if (element) {
        observerRef.current.unobserve(element);
        elementsRef.current.delete(itemKey);
        lastHeightsRef.current.delete(itemKey);
        pendingUpdatesRef.current.delete(itemKey);
      }
    },
    [],
  );

  /**
   * 停止观察所有元素
   */
  const disconnect = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // 清除批量更新定时器
    if (batchUpdateTimerRef.current !== undefined) {
      cancelAnimationFrame(batchUpdateTimerRef.current);
    }

    elementsRef.current.clear();
    lastHeightsRef.current.clear();
    pendingUpdatesRef.current.clear();
  }, []);

  return {
    setObservedElement,
    unobserve,
    disconnect,
  };
}
